import { Link } from "wouter";
import { cn } from "@/lib/utils";
import { taskLinkLabel, type TaskLink } from "@shared/taskLinks";
import { taskLinkHref } from "@shared/recordHref";

/**
 * The records a task is about, each a chip that opens that record's own page.
 * Spec: docs/superpowers/specs/2026-10-26-task-links-design.md
 *
 * One component for every surface (Tasks page, dashboard rail, record-page open
 * items, the task panel), so a link reads and routes the same everywhere.
 * Chips sit beside a row's open-the-panel button, never inside it, and stop
 * the click so it opens the record rather than the task.
 */
export function TaskLinkChips({
  links,
  max,
  className,
}: {
  links: TaskLink[];
  /** Show this many, then "+N" with the rest in its tooltip. All when omitted. */
  max?: number;
  className?: string;
}) {
  if (links.length === 0) return null;
  const shown = max === undefined ? links : links.slice(0, max);
  const rest = links.slice(shown.length);

  return (
    <span className={cn("inline-flex min-w-0 flex-wrap items-center gap-1", className)}>
      {shown.map((l) => {
        const label = taskLinkLabel(l);
        return (
          <Link
            key={`${l.kind}:${l.id}`}
            href={taskLinkHref(l)}
            title={label}
            onClick={(e) => e.stopPropagation()}
            className="inline-block max-w-[220px] truncate rounded border border-border bg-card px-1.5 py-0.5 text-[11px] leading-none text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary-text"
          >
            {label}
          </Link>
        );
      })}
      {rest.length > 0 && (
        <span
          title={rest.map(taskLinkLabel).join("\n")}
          className="rounded border border-border/60 px-1.5 py-0.5 text-[11px] leading-none tabular-nums text-muted-foreground"
        >
          +{rest.length}
        </span>
      )}
    </span>
  );
}
